import { useCallback } from 'react';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';

import { usePrevious } from '@ts-core/hooks/utils';
import { getErrorMessage } from '@ts-core/lib/apollo';

import { userAccountQuery } from 'graphql/auth/queries';
import { signInMutation, signOutMutation } from 'graphql/auth/mutation';

export default function useAuth() {
  const apolloClient = useApolloClient();

  const { data: newUserData, loading: loadingUser, called, error, refetch: refetchUserAccount } = useQuery(userAccountQuery);

  const previousData = usePrevious(newUserData);
  const data = newUserData || previousData;

  const user = data?.userAccount;

  const authLoaded = Boolean(called && (data || error));
  const authenticated = Boolean(user?._id);

  /**
   * Sign in
   *
   * After signin succeeds the server has set the `check.sid` cookie.
   * Refetching the profile from the network is more reliable than
   * `writeQuery` here: apollo-link-rest does not always re-broadcast
   * to subscribers of @rest-decorated queries after a manual cache
   * write, leaving `authenticated` stale and the LoginPage stuck.
   * The refetch goes to `/v2/admin/profile`, the cookie is sent
   * automatically, the user comes back, and the cache update flows
   * through normally so `useAuthState()` picks up `authenticated: true`.
   */
  const [signIn, { loading: signingIn, error: signInError }] = useMutation(signInMutation, {
    onCompleted: () => {
      refetchUserAccount();
    },
  });
  const onSignIn = useCallback(
    (input) => {
      signIn({ variables: { input } });
    },
    [signIn]
  );

  /**
   * Sign out
   */
  const [signOut] = useMutation(signOutMutation, {
    onCompleted: () => {
      apolloClient.cache.reset();
    },
    onError: () => {
      apolloClient.cache.reset();
    },
  });
  const onSignOut = useCallback(() => {
    signOut();
  }, [signOut]);

  return {
    authenticated,
    authLoaded,
    loadingUser,
    user,

    signingIn,
    signInError: getErrorMessage({ error: signInError }),
    onSignIn,
    onSignOut,
  };
}
